import { faCirclePlus } from "@fortawesome/pro-regular-svg-icons/faCirclePlus";
import { faCommentPlus } from "@fortawesome/pro-regular-svg-icons/faCommentPlus";
import { faEye } from "@fortawesome/pro-regular-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-regular-svg-icons/faEyeSlash";
import { faHourglassStart } from "@fortawesome/pro-regular-svg-icons/faHourglassStart";
import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { faUserCheck } from "@fortawesome/pro-solid-svg-icons/faUserCheck";
import { faUserTag } from "@fortawesome/pro-solid-svg-icons/faUserTag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, IconButton, Stack } from '@mui/material';
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppImages } from "app/appImages";
import { fetchKnowledgeQuickAccess } from "app/features/menu/menuSlice";
import { store } from "app/store";
import { MprcConfirmDialog, MprcJoinComponents, MprcRequestErrorDialog, MprcRequestIndicator, MprcText } from 'components';
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { MprcCardTypography } from "components/MprcCardTypography";
import { BuildRibbonCards, MprcCardRibbonData, MprcRibbonGenericCard, MprcRibbonList, MprcRibbonListHandle } from "components/MprcRibbonList";
import { ChannelAutoRegisterMode, ChannelSubscriptionFilterBy, ChannelSubscriptionResponse, RequestChannelSubscriptionResult } from 'generated/CoreApi_v1';
import { NUMBER_MAX_INT32 } from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { CardThumbnail } from "views/general/Cards/Common";
import { RequestChannelSubcriptionDrawer } from "views/knowledge/subcriptions/drawers/RequestChannelSubcriptionDrawer";
import { SearchableList, SearchableListHandle } from "./SearchableList";

////////////////////////////////////////////////////////////////////////////////

interface IChannelSubscriptionsProps {
    maxWidth: number;
    gap: number;
    margin: number;
    searchText: string;
    onResetFilter: () => void;
}

export const ChannelSubscriptions = ({ maxWidth, gap, margin, searchText, onResetFilter }: IChannelSubscriptionsProps) => {
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("knowledge");

    const [expandIndex, setExpandIndex] = useState<number | undefined>(undefined);
    const [dialog, setDialog] = useState<any>(null);

    const [posting, setPosting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(null);
    const [emptyDatasets, setEmptyDatasets] = useState<number>(0);

    const listRibbonRefs = useRef<(MprcRibbonListHandle | null)[]>([null, null, null, null]);
    const searchableListRef = useRef<SearchableListHandle | null>(null);

    //
    function ConvertToRibbonData(item: ChannelSubscriptionResponse, index: number): MprcCardRibbonData {

        const title = (
            <MprcCardTypography noWrap variant={"title"} color={"black"}>
                {item.name}
            </MprcCardTypography>
        );

        const pendingSubscription = item.registered == true && item.hideQuickAccess == null;
        const showQuickAccessButton = item.registered == true && !pendingSubscription;

        const counters = [];
        if (item.publicationCount > 0) counters.push(t("Subcriptions.Signup.Channel.PublicationCount", { count: item.publicationCount }));
        if (item.subscriptionCount > 0) counters.push(t("Subcriptions.Signup.Channel.SubcriptionCount", { count: item.subscriptionCount }));

        return (
            {
                id: item.channelId,
                thumbnail: (
                    <CardThumbnail
                        thumbnailUrl={item.thumbnailUrl ?? AppImages.KnowledgeChannelThumbnail}
                        thumbnailLink={KnowledgePaths.FeedChannel(item.channelId)}
                    />
                ),
                body: (
                    <Box height="36px">
                        <Box>
                            <Link to={KnowledgePaths.FeedChannel(item.channelId)}>
                                {title}
                            </Link>
                        </Box>
                        <MprcCardTypography
                            noWrap
                            variant={"caption"}
                            color={"muted"}
                            reverse={false}
                        >
                            {
                                item.isPrivate &&
                                <>
                                    <FontAwesomeIcon icon={faLock} fixedWidth />
                                    &nbsp;
                                </>
                            }
                            <MprcJoinComponents components={counters} separator={<span>&nbsp;&bull;&nbsp;</span>} />
                        </MprcCardTypography>
                    </Box>
                ),
                actions: (
                    <>
                        <Stack direction="row" alignItems="center" justifyContent={showQuickAccessButton ? "space-between" : "flex-end"} sx={{ width: "100%" }}>
                            {
                                showQuickAccessButton &&
                                <IconButton onClick={() => handleQuickAccess(item, index)} color={item.hideQuickAccess ? "warning" : "muted"}>
                                    <FontAwesomeIcon
                                        icon={item.hideQuickAccess ? faEyeSlash : faEye}
                                        title={item.hideQuickAccess ? t("Subcriptions.Signup.HideQuickAccessToolTip") : t("Subcriptions.Signup.QuickAccessToolTip")}
                                    />
                                </IconButton>
                            }

                            {
                                (item.acceptSubscription || item.registered || item.pendingApproval) &&
                                <Stack direction="row" alignItems="center">
                                    {
                                        pendingSubscription &&
                                        <Box ml={1}>
                                            <FontAwesomeIcon
                                                className={AppGlobalClassKeys.clickable}
                                                icon={faHourglassStart}
                                                title={t("Subcriptions.Signup.WaitingProcessingTooltip")}
                                            />
                                        </Box>
                                    }
                                    <IconButton
                                        sx={{ margin: "0px 0px 0px auto" }}
                                        disabled={pendingSubscription}
                                        onClick={() => handleAction(item)}
                                        title={item.registered
                                            ? t("Subcriptions.Signup.Channel.SubscribedToolTip")
                                            : item.pendingApproval
                                                ? t("Subcriptions.Signup.Channel.PendingApprovalToolTip")
                                                : item.autoRegisterMode == ChannelAutoRegisterMode.Free
                                                    ? t("Subcriptions.Signup.Channel.NotSubscribedToolTip")
                                                    : t("Subcriptions.Signup.Channel.SubscriptionWithApprovalToolTip")
                                        }
                                    >
                                        <MprcText color={item.hideQuickAccess == null && !item.registered ? "inherit" : item.registered ? "success" : item.pendingApproval ? "info" : "inherit"}>
                                            <FontAwesomeIcon
                                                icon={
                                                    item.registered
                                                        ? faUserCheck
                                                        : item.pendingApproval
                                                            ? faUserTag
                                                            : item.autoRegisterMode == ChannelAutoRegisterMode.Free
                                                                ? faCirclePlus
                                                                : faCommentPlus
                                                }
                                            />

                                        </MprcText>
                                    </IconButton>
                                </Stack>
                            }
                        </Stack>
                    </>
                ),
            } as MprcCardRibbonData
        );
    }

    ///
    const lists = useMemo(() => ([
        { id: ChannelSubscriptionFilterBy.TopicInterest, title: t("Subcriptions.Signup.Channel.Filter.TopicInterest") },
        { id: ChannelSubscriptionFilterBy.MostActive, title: t("Subcriptions.Signup.Channel.Filter.MoreActive") },
        { id: ChannelSubscriptionFilterBy.Latest, title: t("Subcriptions.Signup.Channel.Filter.MoreRecent") },
        { id: ChannelSubscriptionFilterBy.Registered, title: t("Subcriptions.Signup.Channel.Filter.Registered") },
    ]), []);

    //
    const doReset = () => {
        setEmptyDatasets(0);
        listRibbonRefs.current[0]?.reset();
        listRibbonRefs.current[1]?.reset();
        listRibbonRefs.current[2]?.reset();
        listRibbonRefs.current[3]?.reset();
        searchableListRef.current?.reset();
    };

    ///
    const handleAction = (item: ChannelSubscriptionResponse) => {
        if (item.pendingApproval) {
            enqueueSnackbar(t("Subcriptions.Signup.Channel.PendingApprovalText"), { variant: "info" });
        }
        else if (item.registered == true) {
            setDialog(<UnsubscribeChannel
                channel={item}
                onComplete={(success) => {
                    setDialog(null);
                    if (success) doReset();
                }}
            />
            );
        }
        else if (item.acceptSubscription == true) {
            setDialog(
                <SubscribeChannel
                    channel={item}
                    onComplete={(success) => {
                        setDialog(null);
                        if (success) doReset();
                    }}
                />
            );
        }
    };

    ///
    const handleExpand = (index: number) => () => {
        setExpandIndex(index == expandIndex ? undefined : index);
    };

    ///
    const handleQuickAccess = async (item: ChannelSubscriptionResponse, index: number) => {
        setPosting(true);
        try {
            var response = await api.v1.knowledgeSubcription.toggleHide({ subscriptionId: item.channelId });
            if (response.data) {
                store.dispatch(fetchKnowledgeQuickAccess({}));

                item.hideQuickAccess = !item.hideQuickAccess;

                if (searchText == "") {
                    listRibbonRefs.current[3]?.replaceCard(
                        index,
                        BuildRibbonCards({ items: [ConvertToRibbonData(item, index)], marginWidth: margin })[0],
                        expandIndex != null
                    );
                }
                else {
                    searchableListRef.current?.replaceCard(
                        index,
                        BuildRibbonCards({ items: [ConvertToRibbonData(item, index)], marginWidth: margin })[0]
                    );
                }
            }
            else {
                enqueueSnackbar(t("Error.PostFailed", { ns: "shared" }), { variant: "error" });
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const handleGetData = (filterBy: ChannelSubscriptionFilterBy, expanded: boolean) => {
        return new Promise<React.ReactNode[]>((resolve, reject) => {
            api.v1.knowledgeSubcription.channelSubscriptions({
                filterBy: filterBy,
                takeRecords: expanded ? NUMBER_MAX_INT32 : 21,
                searchText: searchText
            })
                .then((response) => {
                    const items = response.data.map((item, index) => ConvertToRibbonData(item, index));
                    resolve(BuildRibbonCards({ items, marginWidth: margin }));
                })
                .catch(e => reject(e));
        });
    };

    ///
    const handleResetFilters = () => {
        doReset();
        onResetFilter();
    };

    ///
    useEffect(() => {
        searchableListRef.current?.reset();
    }, [searchText]);

    ///
    return (
        <>
            {dialog}

            <MprcRequestIndicator type="post" visible={posting} />

            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            {
                emptyDatasets > 0 && emptyDatasets == lists.length && searchText.length == 0 &&
                <MprcFilterEmptyDataset message={t("Subcriptions.Signup.Channel.EmptyDataset")} />
            }

            {
                searchText == ""
                    ? <>
                        {
                            lists.map((item, index) => {
                                return (
                                    <MprcRibbonList
                                        ref={ref => { if (ref != null) listRibbonRefs.current[index] = ref; }}
                                        key={index}
                                        id={item.id}
                                        index={index}
                                        gapWidth={gap}
                                        slideWidth={maxWidth + gap}
                                        title={item.title}
                                        maxItems={20}
                                        ribbonCardLoadMore={
                                            MprcRibbonGenericCard({
                                                message: t("Subcriptions.Signup.ShowMoreCards"),
                                                marginWidth: margin,
                                                onClick: () => setExpandIndex(index)
                                            })
                                        }
                                        expanded={expandIndex === index}
                                        visible={expandIndex == undefined || expandIndex === index}
                                        onExpandClick={handleExpand(index)}
                                        onGetCards={(id, expanded) => handleGetData(id as ChannelSubscriptionFilterBy, expanded)}
                                        onEmptyDataSet={() => setEmptyDatasets(state => state + 1)}
                                    />
                                );
                            })
                        }
                    </>
                    : <SearchableList
                        ref={searchableListRef}
                        gapWidth={gap}
                        noRecordsMessage={t("Subcriptions.Signup.Channel.NoRecordsFoundMessage")}
                        filterText={(count) => t("Subcriptions.Signup.Channel.CountText", { count })}
                        onResetFilter={handleResetFilters}
                        onGetCards={() => handleGetData(ChannelSubscriptionFilterBy.SearchText, true)}
                    />
            }
        </>
    );
};


////////////////////////////////////////////////////////////////////////////////

interface IChannelData {
    channelId: string;
    name: string;
    indirectMember: boolean;
    pendingApproval: boolean;
    registered: boolean;
    autoRegisterMode: ChannelAutoRegisterMode;
    readonly?: boolean;
}

export interface SubscribeChannelProps {
    channel: IChannelData;
    onComplete: (success: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////////

export const UnsubscribeChannel: React.FC<SubscribeChannelProps> = (props) => {

    const { channel: { channelId, pendingApproval, indirectMember }, onComplete } = props;

    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("knowledge");
    const api = useCoreApi();

    const [posting, setPosting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(null);

    ///
    const doUnsubscribe = async (channelId: string) => {
        setPosting(true);
        try {
            var response = await api.v1.knowledgeSubcription.unsubscribeChannel({ channelId });
            if (response.data) {
                enqueueSnackbar(t("Subcriptions.Signup.Channel.UnsubscribeSuccessMessage"), { variant: "success" });
                onComplete(true);
                store.dispatch(fetchKnowledgeQuickAccess({}));
            }
            else {
                enqueueSnackbar(t("Error.PostFailed", { ns: "shared" }), { variant: "error" });
                onComplete(false);
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    if (pendingApproval) {
        enqueueSnackbar(t("Subcriptions.Signup.Channel.PendingApprovalText"), { variant: "info" });
        onComplete(false);
        return null;
    }

    return (
        <>
            <MprcConfirmDialog
                open
                message={t("Subcriptions.Signup.Channel.UnsubscribeText")}
                onClose={
                    (confirm) => {
                        if (confirm) {
                            if (indirectMember) {
                                enqueueSnackbar(t("Subcriptions.Signup.Channel.UnsubscribeIndirectMemberText"), { variant: "error" });
                                onComplete(false);
                            }
                            else {
                                doUnsubscribe(channelId);
                            }
                        }
                        else {
                            onComplete(false);
                        }
                    }
                }
            />

            <MprcRequestIndicator type="post" visible={posting} />

            <MprcRequestErrorDialog error={viewError} onClose={() => onComplete(false)} />
        </>
    );
};

////////////////////////////////////////////////////////////////////////////////

export const SubscribeChannel: React.FC<SubscribeChannelProps> = (props) => {

    const { channel: { channelId, name, autoRegisterMode }, onComplete } = props;

    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("knowledge");
    const api = useCoreApi();

    const [dialog, setDialog] = useState<any>();
    const [posting, setPosting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(null);

    ///
    const doFreeSubscribe = async (channelId: string) => {
        setPosting(true);
        try {
            var response = await api.v1.knowledgeSubcription.requestChannelSubscription({ channelId, message: null, password: null, onlyIfFreeRegistration: true });
            if (response.data == RequestChannelSubscriptionResult.Subscribed) {
                enqueueSnackbar(t("Subcriptions.Signup.Channel.SubscribeSuccessMessage"), { variant: "success" });
                onComplete(true);
                store.dispatch(fetchKnowledgeQuickAccess({}));
            }
            else {
                enqueueSnackbar(t("Subcriptions.Signup.Channel.SubscribeFailedMessage"), { variant: "error" });
                onComplete(false);
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    useEffect(() => {
        if (autoRegisterMode == ChannelAutoRegisterMode.Free) {
            doFreeSubscribe(channelId);
        }
        else {
            setDialog(
                <RequestChannelSubcriptionDrawer
                    channelId={channelId}
                    subtitle={name}
                    onClose={(reload) => onComplete(reload)}
                />
            );
        }
    }, []);

    return (
        <>
            {dialog}

            <MprcRequestIndicator type="post" visible={posting} />

            <MprcRequestErrorDialog error={viewError} onClose={() => onComplete(false)} />
        </>
    );
};
